import React, { useState } from 'react';
import { 
  Button, 
  Table, 
  Input, 
  Space, 
  Breadcrumb, 
  Card,
  Row,
  Col,
  Form,
  Select,
  DatePicker,
  Tag,
  Modal,
  Tabs,
  message,
  Statistic,
  List,
  Avatar,
  Rate
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  EyeOutlined, 
  EditOutlined,
  ReloadOutlined,
  DownloadOutlined,
  SettingOutlined,
  SearchOutlined,
  DownOutlined,
  UpOutlined,
  UserSwitchOutlined,
  UserOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';

const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// 添加客户数据接口定义
interface CustomerData {
  key: string;
  name: string;
  phone: string;
  level: string;
  totalSpent: number;
  lastOrderTime: string;
  lastOrderAmount: number;
  source: string;
}

interface EmployeeData {
  key: string;
  employeeId: string;
  name: string;
  idCard: string;
  phone: string;
  department: string;
  position: string;
  role: string;
  status: 'active' | 'inactive' | 'resigned';
  boundCustomers: number;
  joinDate: string;
  resignDate?: string;
}

const EmployeeManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [searchCollapsed, setSearchCollapsed] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  
  // 添加新的状态和函数
  const [customerListVisible, setCustomerListVisible] = useState(false);
  const [customerListLoading, setCustomerListLoading] = useState(false);
  const [customerList, setCustomerList] = useState<CustomerData[]>([]);
  
  // 添加新的状态
  const [customerTabKey, setCustomerTabKey] = useState<string>('customers');
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerData | null>(null);
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [tagForm] = Form.useForm();
  const [customerTags, setCustomerTags] = useState<Record<string, string[]>>({
    '1': ['高消费', '生日特权'],
    '2': ['新客户'],
    '3': ['定期消费'],
  });

  // 示例数据
  const employees: EmployeeData[] = [
    {
      key: '1',
      employeeId: 'EMP001',
      name: '张三',
      idCard: '110101199001011234',
      phone: '13800138001',
      department: '客房部',
      position: '高级管家',
      role: '管理员',
      status: 'active',
      boundCustomers: 48,
      joinDate: '2022-05-10',
    },
    {
      key: '2',
      employeeId: 'EMP002',
      name: '李四',
      idCard: '110101199002022345',
      phone: '13800138002',
      department: '餐饮部',
      position: '餐饮顾问',
      role: '普通员工',
      status: 'active',
      boundCustomers: 32,
      joinDate: '2022-06-15',
    },
    {
      key: '3',
      employeeId: 'EMP003',
      name: '王五',
      idCard: '110101199003033456',
      phone: '13800138003',
      department: '客房部',
      position: '管家',
      role: '普通员工',
      status: 'resigned',
      boundCustomers: 27,
      joinDate: '2021-11-20',
      resignDate: '2023-04-15',
    },
    {
      key: '4',
      employeeId: 'EMP004',
      name: '赵六',
      idCard: '110101199004044567',
      phone: '13800138004',
      department: '前厅部',
      position: '前台接待',
      role: '普通员工',
      status: 'active',
      boundCustomers: 15,
      joinDate: '2023-01-05',
    },
    {
      key: '5',
      employeeId: 'EMP005',
      name: '钱七',
      idCard: '110101199005055678',
      phone: '13800138005',
      department: '餐饮部',
      position: '管家',
      role: '普通员工',
      status: 'inactive',
      boundCustomers: 8,
      joinDate: '2023-03-12',
    },
  ];
  
  const [employeeList, setEmployeeList] = useState<EmployeeData[]>(employees);

  const columns: ColumnsType<EmployeeData> = [
    {
      title: '工号',
      dataIndex: 'employeeId',
      key: 'employeeId',
      fixed: 'left',
      width: 100,
    },
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      fixed: 'left',
      width: 100,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: '身份证号',
      dataIndex: 'idCard',
      key: 'idCard',
      render: (text: string) => text.replace(/^(.{6})(?:\d+)(.{4})$/, '$1****$2'),
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department',
    },
    {
      title: '职位',
      dataIndex: 'position',
      key: 'position',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={
          status === 'active' ? 'success' : 
          status === 'inactive' ? 'warning' : 'error'
        }>
          {status === 'active' ? '在职' : 
           status === 'inactive' ? '停用' : '离职'}
        </Tag>
      ),
    },
    {
      title: '绑定客户数',
      dataIndex: 'boundCustomers',
      key: 'boundCustomers',
      render: (text: number, record: EmployeeData) => (
        <a onClick={() => handleViewCustomers(record)}>{text}</a>
      ),
    },
    {
      title: '入职日期',
      dataIndex: 'joinDate',
      key: 'joinDate',
    },
    {
      title: '离职日期',
      dataIndex: 'resignDate',
      key: 'resignDate',
      render: (text: string) => text || '-',
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 200,
      render: (_: any, record: EmployeeData) => (
        <Space size="middle">
          <Button type="link" icon={<EyeOutlined />} style={{ padding: 0 }}>查看详情</Button>
          <Button type="link" icon={<EditOutlined />} style={{ padding: 0 }}>编辑</Button>
          {record.status === 'resigned' && (
            <Button 
              type="link" 
              icon={<UserSwitchOutlined />}
              style={{ padding: 0 }} 
              onClick={() => {
                setSelectedEmployee(record);
                setTransferModalVisible(true);
              }}
            >
              客户转移
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const handleSearch = () => {
    const formValues = form.getFieldsValue();
    // 实际应用中会根据表单数据过滤
    console.log('搜索条件:', formValues);
    message.success('搜索完成');
  };

  const handleReset = () => {
    form.resetFields();
    setEmployeeList(employees);
  };

  const handleTransferCustomers = () => {
    // 实际应用中会处理客户转移逻辑
    message.success(`已将${selectedEmployee?.name}的客户成功转移`);
    setTransferModalVisible(false);
  };

  const handleBatchTransfer = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要转移的员工');
      return;
    }
    message.info('批量转移功能待实现');
  };

  // 处理查看客户列表
  const handleViewCustomers = (employee: EmployeeData) => {
    setSelectedEmployee(employee);
    setCustomerListLoading(true);
    setCustomerListVisible(true);
    
    // 模拟加载数据
    setTimeout(() => {
      // 这里应该是从API获取数据，现在使用模拟数据
      const mockCustomers: CustomerData[] = [
        {
          key: '1',
          name: '客户A',
          phone: '13900001111',
          level: '黑钻卡',
          totalSpent: 12580,
          lastOrderTime: '2023-05-15',
          lastOrderAmount: 2380,
          source: '员工推广',
        },
        {
          key: '2',
          name: '客户B',
          phone: '13800002222',
          level: '普卡',
          totalSpent: 5680,
          lastOrderTime: '2023-04-20',
          lastOrderAmount: 1280,
          source: '扫码获客',
        },
        {
          key: '3',
          name: '客户C',
          phone: '13700003333',
          level: '金卡',
          totalSpent: 8960,
          lastOrderTime: '2023-05-10',
          lastOrderAmount: 1680,
          source: '员工推广',
        },
        {
          key: '4',
          name: '客户D',
          phone: '13600004444',
          level: '普卡',
          totalSpent: 3250,
          lastOrderTime: '2023-03-25',
          lastOrderAmount: 980,
          source: '员工推广',
        },
        {
          key: '5',
          name: '客户E',
          phone: '13500005555',
          level: '铂金卡',
          totalSpent: 15800,
          lastOrderTime: '2023-05-18',
          lastOrderAmount: 3280,
          source: '扫码获客',
        },
      ];
      
      setCustomerList(mockCustomers);
      setCustomerListLoading(false);
    }, 800);
  };

  // 获取模拟订单数据的函数
  const getOrderData = () => {
    // 如果有选中的客户，只返回该客户的订单
    if (selectedCustomer) {
      return mockOrderData.filter(order => order.customerId === selectedCustomer.key);
    }
    return mockOrderData;
  };
  
  // 获取模拟评价数据的函数
  const getReviewData = () => {
    // 如果有选中的客户，只返回该客户的评价
    if (selectedCustomer) {
      return mockReviewData.filter(review => review.customerId === selectedCustomer.key);
    }
    return mockReviewData;
  };

  // 模拟订单数据
  const mockOrderData = [
    {
      orderId: 'ORD20230501001',
      customerId: '1',
      customerName: '客户A',
      memberLevel: '黑钻卡',
      itemName: '豪华套房 - 2晚',
      amount: 2380,
      orderTime: '2023-05-15 14:30',
      paymentMethod: '微信支付',
      status: '已完成'
    },
    {
      orderId: 'ORD20230425002',
      customerId: '2',
      customerName: '客户B',
      memberLevel: '普卡',
      itemName: '标准双人房 - 1晚',
      amount: 680,
      orderTime: '2023-04-25 10:15',
      paymentMethod: '支付宝',
      status: '已完成'
    },
    {
      orderId: 'ORD20230510003',
      customerId: '3',
      customerName: '客户C',
      memberLevel: '金卡',
      itemName: '高级单人房 - 3晚',
      amount: 1680,
      orderTime: '2023-05-10 18:45',
      paymentMethod: '银行卡',
      status: '已完成'
    },
    {
      orderId: 'ORD20230518004',
      customerId: '5',
      customerName: '客户E',
      memberLevel: '铂金卡',
      itemName: '总统套房 - 1晚',
      amount: 3280,
      orderTime: '2023-05-18 09:20',
      paymentMethod: '微信支付',
      status: '已完成'
    },
    {
      orderId: 'ORD20230519005',
      customerId: '1',
      customerName: '客户A',
      memberLevel: '黑钻卡',
      itemName: '餐厅消费 - 法式晚餐',
      amount: 860,
      orderTime: '2023-05-19 20:10',
      paymentMethod: '房间记账',
      status: '处理中'
    },
    {
      orderId: 'ORD20230520006',
      customerId: '4',
      customerName: '客户D',
      memberLevel: '普卡',
      itemName: 'SPA护理套餐',
      amount: 980,
      orderTime: '2023-05-20 15:30',
      paymentMethod: '会员积分',
      status: '已预约'
    }
  ];
  
  // 模拟评价数据
  const mockReviewData = [
    {
      id: '1',
      customerId: '1',
      customerName: '客户A',
      rating: 5,
      content: '房间很干净，服务周到，环境舒适，下次还会选择这家酒店。服务人员专业且友好，前台办理入住非常高效。',
      reviewTime: '2023-05-16 10:20',
      serviceType: '住宿服务',
      orderAmount: 2380
    },
    {
      id: '2',
      customerId: '2',
      customerName: '客户B',
      rating: 4,
      content: '整体体验不错，但房间隔音稍差，能听到走廊的声音。床铺舒适度很好，早餐品种丰富。',
      reviewTime: '2023-04-26 08:15',
      serviceType: '住宿服务',
      orderAmount: 680
    },
    {
      id: '3',
      customerId: '3',
      customerName: '客户C',
      rating: 3,
      content: '服务一般，退房时等待时间有点长。房间设施还算齐全，但有些老旧，卫生间有异味。',
      reviewTime: '2023-05-13 14:40',
      serviceType: '住宿服务',
      orderAmount: 1680
    },
    {
      id: '4',
      customerId: '5',
      customerName: '客户E',
      rating: 5,
      content: '总统套房非常奢华，视野开阔，服务一流，值得推荐！酒店接待非常周到，提供了免费升级服务。',
      reviewTime: '2023-05-19 16:25',
      serviceType: '住宿服务',
      orderAmount: 3280
    },
    {
      id: '5',
      customerId: '1',
      customerName: '客户A',
      rating: 4,
      content: '法式晚餐味道不错，服务态度好，但上菜速度可以再快一些。餐厅环境优雅，氛围很好。',
      reviewTime: '2023-05-20 09:30',
      serviceType: '餐饮服务',
      orderAmount: 860
    }
  ];

  // 添加打标签的处理函数
  const handleOpenTagModal = (customer: CustomerData) => {
    setSelectedCustomer(customer);
    setTagModalVisible(true);
    // 设置表单初始值
    tagForm.setFieldsValue({
      tags: customerTags[customer.key] || []
    });
  };

  const handleTagSubmit = () => {
    tagForm.validateFields().then(values => {
      // 更新客户标签
      setCustomerTags({
        ...customerTags,
        [selectedCustomer!.key]: values.tags
      });
      
      message.success('标签设置成功!');
      setTagModalVisible(false);
    });
  };

  const predefinedTags = [
    '高消费', '新客户', '定期消费', '生日特权', '商务客户', 
    '休闲旅游', '长住客人', '需特殊关注', '敏感客户', 'VIP客户'
  ];

  // 修改客户列表的列定义，添加操作列和标签列
  const customerColumns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 100,
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
    },
    {
      title: '会员等级',
      dataIndex: 'level',
      key: 'level',
      width: 100,
      render: (text: string) => (
        <Tag color={
          text === '黑钻卡' ? 'black' :
          text === '铂金卡' ? 'purple' :
          text === '金卡' ? 'gold' :
          text === '银卡' ? 'silver' : 'blue'
        }>
          {text}
        </Tag>
      )
    },
    {
      title: '累计消费',
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      width: 120,
      render: (text: number) => `¥${text.toLocaleString()}`,
    },
    {
      title: '最近消费',
      dataIndex: 'lastOrderTime',
      key: 'lastOrderTime',
      width: 120,
    },
    {
      title: '最近消费金额',
      dataIndex: 'lastOrderAmount',
      key: 'lastOrderAmount',
      width: 120,
      render: (text: number) => `¥${text.toLocaleString()}`,
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 100,
    },
    {
      title: '标签',
      key: 'tags',
      render: (_: unknown, record: CustomerData) => (
        <>
          {customerTags[record.key]?.map(tag => (
            <Tag color="blue" key={tag}>
              {tag}
            </Tag>
          ))}
        </>
      )
    },
    {
      title: '操作',
      key: 'action',
      render: (_: unknown, record: CustomerData) => (
        <Space size="middle">
          <Button 
            type="link" 
            size="small" 
            onClick={() => {
              setSelectedCustomer(record);
              setCustomerTabKey('orders');
            }}
          >
            消费记录
          </Button>
          <Button 
            type="link" 
            size="small" 
            onClick={() => handleOpenTagModal(record)}
          >
            打标签
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="employee-management">
      <Breadcrumb 
        style={{ marginBottom: 16 }} 
        items={[
          { title: '首页' },
          { title: '员工管理' },
        ]}
      />
      
      <Card 
        style={{ marginBottom: 16 }}
        bordered={false}
        className="search-form-card"
      >
        <Form
          form={form}
          layout="horizontal"
          onFinish={handleSearch}
        >
          <Row gutter={[24, 16]} style={{ display: searchCollapsed ? 'none' : 'flex' }}>
            <Col span={6}>
              <Form.Item label="工号" name="employeeId">
                <Input placeholder="请输入" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="员工姓名" name="employeeName">
                <Input placeholder="请输入" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="手机号" name="phone">
                <Input placeholder="请输入" allowClear />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="身份证号" name="idCard">
                <Input placeholder="请输入" allowClear />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={[24, 16]} style={{ display: searchCollapsed ? 'none' : 'flex' }}>
            <Col span={6}>
              <Form.Item label="部门" name="department">
                <Select
                  placeholder="请选择"
                  allowClear
                  options={[
                    { value: '客房部', label: '客房部' },
                    { value: '餐饮部', label: '餐饮部' },
                    { value: '前厅部', label: '前厅部' },
                  ]}
                  suffixIcon={<DownOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="角色" name="role">
                <Select
                  placeholder="请选择"
                  allowClear
                  options={[
                    { value: '管理员', label: '管理员' },
                    { value: '普通员工', label: '普通员工' },
                  ]}
                  suffixIcon={<DownOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="状态" name="status">
                <Select
                  placeholder="请选择"
                  allowClear
                  options={[
                    { value: 'active', label: '在职' },
                    { value: 'inactive', label: '停用' },
                    { value: 'resigned', label: '离职' },
                  ]}
                  suffixIcon={<DownOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="入职日期" name="joinDate">
                <DatePicker style={{ width: '100%' }} placeholder="请选择日期" />
              </Form.Item>
            </Col>
          </Row>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            marginTop: 24 
          }}>
            <Button onClick={handleReset}>重置</Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              icon={<SearchOutlined />}
              style={{ marginLeft: 8 }}
            >
              查询
            </Button>
            <a 
              style={{ marginLeft: 8 }}
              onClick={() => setSearchCollapsed(!searchCollapsed)}
            >
              {searchCollapsed ? '展开' : '收起'} {searchCollapsed ? <DownOutlined /> : <UpOutlined />}
            </a>
          </div>
        </Form>
      </Card>

      <Card bordered={false}>
        <Tabs defaultActiveKey="1">
          <TabPane tab="员工列表" key="1">
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
              <Space>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />}
                >
                  新增员工
                </Button>
                <Button 
                  icon={<UserSwitchOutlined />} 
                  disabled={selectedRowKeys.length === 0}
                  onClick={handleBatchTransfer}
                >
                  批量转移 {selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ''}
                </Button>
              </Space>
              <Space>
                <Button icon={<ReloadOutlined />}>刷新</Button>
                <Button icon={<DownloadOutlined />}>导出</Button>
                <Button icon={<SettingOutlined />}>设置</Button>
              </Space>
            </div>

            <Table 
              columns={columns} 
              dataSource={employeeList}
              rowSelection={rowSelection}
              scroll={{ x: 1500 }}
              pagination={{
                total: employeeList.length,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `总共 ${total} 条`
              }}
            />
          </TabPane>
          
          <TabPane tab="绑定关系查询" key="2">
            <Form layout="inline" style={{ marginBottom: 16 }}>
              <Form.Item label="客户手机号">
                <Input placeholder="请输入客户手机号" allowClear />
              </Form.Item>
              <Form.Item label="员工姓名">
                <Input placeholder="请输入员工姓名" allowClear />
              </Form.Item>
              <Form.Item>
                <Button type="primary" icon={<SearchOutlined />}>查询</Button>
              </Form.Item>
            </Form>
            <div style={{ padding: '40px 0', textAlign: 'center', background: '#f5f5f5' }}>
              请输入查询条件
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {/* 客户转移弹窗 */}
      <Modal
        title="客户转移"
        open={transferModalVisible}
        onOk={handleTransferCustomers}
        onCancel={() => setTransferModalVisible(false)}
        width={600}
      >
        <Form layout="vertical">
          <Form.Item 
            label="离职员工" 
            name="sourceEmployee"
            initialValue={selectedEmployee?.name}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item 
            label="待转移客户数" 
            name="customerCount"
            initialValue={selectedEmployee?.boundCustomers}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item 
            label="转移至员工" 
            name="targetEmployee"
            rules={[{ required: true, message: '请选择接收客户的员工' }]}
          >
            <Select
              placeholder="请选择员工"
              options={employees
                .filter(e => e.status === 'active')
                .map(e => ({ label: e.name, value: e.key }))}
              suffixIcon={<DownOutlined />}
            />
          </Form.Item>
          <Form.Item 
            label="转移原因" 
            name="reason"
          >
            <Input.TextArea rows={4} placeholder="请输入转移原因" />
          </Form.Item>
        </Form>
      </Modal>
      
      {/* 修改客户列表弹窗，改为Tab切换方式 */}
      <Modal
        title={`${selectedEmployee?.name || ''} - 客户数据`}
        open={customerListVisible}
        onCancel={() => setCustomerListVisible(false)}
        footer={null}
        width={900}
        bodyStyle={{ padding: '0 0 16px 0' }}
      >
        <div style={{ padding: '16px 24px 0' }}>
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card size="small">
                <Statistic 
                  title="客户总数" 
                  value={customerList.length} 
                  prefix={<UserOutlined />} 
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic 
                  title="累计消费" 
                  value={customerList.reduce((sum, cur) => sum + cur.totalSpent, 0)} 
                  precision={2} 
                  prefix="¥" 
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card size="small">
                <Statistic 
                  title="高级会员占比" 
                  value={customerList.filter(c => c.level === '金卡' || c.level === '铂金卡' || c.level === '黑钻卡').length / (customerList.length || 1) * 100} 
                  precision={1} 
                  suffix="%" 
                />
              </Card>
            </Col>
          </Row>
        </div>
        
        <div style={{ padding: '0 24px' }}>
          <Tabs activeKey={customerTabKey} onChange={setCustomerTabKey}>
            <TabPane tab="客户列表" key="customers">
              <Table
                dataSource={customerList}
                loading={customerListLoading}
                rowKey="key"
                pagination={{ pageSize: 5 }}
                columns={customerColumns}
                scroll={{ x: 970 }}
              />
            </TabPane>
            
            <TabPane tab="消费记录" key="orders">
              <div style={{ marginBottom: 16 }}>
                <Form layout="inline">
                  <Form.Item label="客户筛选">
                    <Select
                      style={{ width: 200 }}
                      placeholder="选择客户"
                      value={selectedCustomer?.key}
                      onChange={(value) => {
                        const customer = customerList.find(c => c.key === value);
                        setSelectedCustomer(customer || null);
                      }}
                      allowClear
                      options={customerList.map(c => ({ label: `${c.name} (${c.phone})`, value: c.key }))}
                    />
                  </Form.Item>
                  <Form.Item label="消费时间">
                    <RangePicker />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" icon={<SearchOutlined />}>查询</Button>
                  </Form.Item>
                </Form>
              </div>
              
              <Table
                dataSource={getOrderData()}
                loading={customerListLoading}
                rowKey="orderId"
                pagination={{ pageSize: 5 }}
                columns={[
                  {
                    title: '订单编号',
                    dataIndex: 'orderId',
                    key: 'orderId',
                    width: 120,
                  },
                  {
                    title: '客户',
                    dataIndex: 'customerName',
                    key: 'customerName',
                    width: 100,
                    render: (text, record) => (
                      <span>
                        {text} 
                        {record.memberLevel && 
                          <Tag color={
                            record.memberLevel === '黑钻卡' ? 'black' :
                            record.memberLevel === '铂金卡' ? 'purple' :
                            record.memberLevel === '金卡' ? 'gold' :
                            record.memberLevel === '银卡' ? 'silver' : 'blue'
                          }>
                            {record.memberLevel}
                          </Tag>
                        }
                      </span>
                    )
                  },
                  {
                    title: '消费项目',
                    dataIndex: 'itemName',
                    key: 'itemName',
                    width: 150,
                  },
                  {
                    title: '消费金额',
                    dataIndex: 'amount',
                    key: 'amount',
                    width: 120,
                    render: (text: number) => `¥${text.toLocaleString()}`,
                  },
                  {
                    title: '消费时间',
                    dataIndex: 'orderTime',
                    key: 'orderTime',
                    width: 150,
                  },
                  {
                    title: '支付方式',
                    dataIndex: 'paymentMethod',
                    key: 'paymentMethod',
                    width: 100,
                  },
                  {
                    title: '状态',
                    dataIndex: 'status',
                    key: 'status',
                    width: 100,
                    render: (text: string) => (
                      <Tag color={
                        text === '已完成' ? 'green' :
                        text === '处理中' ? 'blue' : 'orange'
                      }>
                        {text}
                      </Tag>
                    )
                  },
                  {
                    title: '操作',
                    key: 'action',
                    fixed: 'right',
                    width: 100,
                    render: () => (
                      <Space size="small">
                        <Button type="link" size="small">详情</Button>
                      </Space>
                    ),
                  },
                ]}
                scroll={{ x: 970 }}
              />
            </TabPane>
            
            <TabPane tab="服务评价" key="reviews">
              <div style={{ marginBottom: 16 }}>
                <Form layout="inline">
                  <Form.Item label="客户筛选">
                    <Select
                      style={{ width: 200 }}
                      placeholder="选择客户"
                      allowClear
                      options={customerList.map(c => ({ label: `${c.name} (${c.phone})`, value: c.key }))}
                    />
                  </Form.Item>
                  <Form.Item label="评价等级">
                    <Select
                      style={{ width: 120 }}
                      placeholder="评价等级"
                      allowClear
                      options={[
                        { label: '5星', value: 5 },
                        { label: '4星', value: 4 },
                        { label: '3星', value: 3 },
                        { label: '2星', value: 2 },
                        { label: '1星', value: 1 },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" icon={<SearchOutlined />}>查询</Button>
                  </Form.Item>
                </Form>
              </div>
              
              <List
                itemLayout="horizontal"
                dataSource={getReviewData()}
                renderItem={item => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<Avatar size={40}>{item.customerName.charAt(0)}</Avatar>}
                      title={
                        <div>
                          <span style={{ marginRight: 8 }}>{item.customerName}</span>
                          <Rate disabled defaultValue={item.rating} />
                          <span style={{ marginLeft: 8, color: '#999' }}>{item.reviewTime}</span>
                        </div>
                      }
                      description={item.content}
                    />
                    <div>
                      <Tag color="blue">{item.serviceType}</Tag>
                      <div style={{ color: '#ff4d4f', marginTop: 4 }}>¥{item.orderAmount.toLocaleString()}</div>
                    </div>
                  </List.Item>
                )}
                pagination={{ pageSize: 5 }}
              />
            </TabPane>
          </Tabs>
        </div>
      </Modal>

      {/* 客户标签设置模态框 */}
      <Modal
        title="客户标签设置"
        open={tagModalVisible}
        onOk={handleTagSubmit}
        onCancel={() => setTagModalVisible(false)}
      >
        {selectedCustomer && (
          <div style={{ marginBottom: 16 }}>
            <p><strong>客户姓名:</strong> {selectedCustomer.name}</p>
            <p><strong>客户等级:</strong> {selectedCustomer.level}</p>
          </div>
        )}
        <Form form={tagForm}>
          <Form.Item
            name="tags"
            label="标签"
            rules={[{ required: true, message: '请至少选择一个标签' }]}
          >
            <Select
              mode="multiple"
              placeholder="请选择标签"
              style={{ width: '100%' }}
              tokenSeparators={[',']}
            >
              {predefinedTags.map(tag => (
                <Select.Option key={tag} value={tag}>
                  {tag}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeManagement; 